/**
 * Site Header
 *
 * Top navigation bar for the SRAC site: logo, core nav links, weather
 * indicator, inbox badge, and the account menu (profile, membership card
 * preview, admin links, sign-in/sign-out). Sign-out also terminates the
 * client Firestore instance and clears its persistent IndexedDB cache so
 * locally cached member data does not outlive the session on a shared
 * device.
 *
 * Copyright — see the LICENSE file in the repository root for terms.
 */

'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { Logo } from '@/components/layout/logo';
import { Sidebar } from '@/components/layout/sidebar';
import { SocialMediaLinks } from '@/components/features/social-media-links';
import { WeatherIndicator } from '@/components/features/weather-indicator';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  LogIn,
  LogOut,
  ChevronDown,
  UserCircle,
  CreditCard,
  ShoppingCart,
  Inbox,
  MailCheck,
  Home,
  Info,
  Calendar,
  Image as ImageIcon,
} from 'lucide-react';
import { useUser } from '@/firebase/auth/use-user';
import { useUserData } from '@/hooks/use-user-data';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { signOut } from 'firebase/auth';
import { useAuth, useDoc, useFirestore, useCollection } from '@/firebase/index';
import {
  doc,
  collection,
  getDocs,
  getDoc,
  query,
  where,
  terminate,
  clearIndexedDbPersistence,
} from 'firebase/firestore';
import { Skeleton } from '@/components/ui/skeleton';
import MemberCardModal, {
  type CardHolder,
} from '@/components/features/member-card-modal';
import type { MembershipCardConfig } from '@/models/membership-card-config';
import type { Profile } from '@/models/profile';
import { membershipInfoQuery } from '@/lib/membership-info-access';
import type { Membership } from '@/models/membership';
import type { Message } from '@/models/message';

// Core top navigation (simplified)
const coreNavLinks = [
  { href: '/', label: 'Home', icon: Home },
  { href: '/about', label: 'About', icon: Info },
  { href: '/events', label: 'Events', icon: Calendar },
  { href: '/gallery', label: 'Gallery', icon: ImageIcon },
  { href: '/membership', label: 'Membership', icon: CreditCard },
];

const UserNav = () => {
  const { user, loading } = useUser();
  const { isEmailVerified } = useUserData();
  const auth = useAuth();
  const firestore = useFirestore();
  const [showCardDialog, setShowCardDialog] = useState(false);
  const [cardHolders, setCardHolders] = useState<CardHolder[]>([]);
  const [cardConfig, setCardConfig] = useState<MembershipCardConfig | null>(null);
  const [sharedData, setSharedData] = useState<Record<string, any>>({});
  const [membership, setMembership] = useState<Membership | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);

  const userProfileRef = useMemo(() => {
    return user ? doc(firestore, 'users', user.uid) : null;
  }, [user, firestore]);
  
  const { data: userProfile, loading: profileLoading } = useDoc(userProfileRef);

  const isAdmin = (userProfile as any)?.isAdmin;
  const isOfficer = (userProfile as any)?.isOfficer;
  const isMember = (userProfile as any)?.isMember;
  const isCD = ((userProfile as any)?.cdTypes ?? []).length > 0;
  const hasAdminAccess = isAdmin || isOfficer; // Officers also get admin access

  const handleSignOut = async () => {
    if (auth) {
      await signOut(auth);
    }
    // Purge the persistent Firestore cache (IndexedDB) so documents this
    // account fetched — registrations, member contact info, etc. — don't
    // outlive the session on a shared device, then hard-reload for a
    // fresh SDK instance (the terminated one is unusable).
    try {
      if (firestore) {
        await terminate(firestore);
        await clearIndexedDbPersistence(firestore);
      }
    } catch {
      /* best-effort — an unsupported browser just keeps the default cache */
    }
    window.location.assign('/');
  };

  const handleViewCard = async () => {
    try {
      // Load card configuration
      const configRef = doc(firestore, 'membershipCardConfig', 'default');
      const configSnap = await getDoc(configRef);
      if (configSnap.exists()) {
        setCardConfig(configSnap.data() as MembershipCardConfig);
      }

      // Load shared data (membershipInfo)
      const membershipInfoSnap = await getDocs(
        membershipInfoQuery(firestore, {
          signedIn: Boolean(user),
          hasMembership: isMember,
          isOfficerOrAdmin: Boolean(userProfile?.isOfficer || userProfile?.isAdmin),
        }),
      );
      const loadedSharedData: Record<string, any> = {};
      membershipInfoSnap.forEach((doc) => {
        const data = doc.data();
        if (data.name && data.value !== undefined) {
          loadedSharedData[data.name] = data.value;
        }
      });
      setSharedData(loadedSharedData);

      // Load membership data if linked
      if ((userProfile as any)?.membershipId) {
        // First, load the linked membership
        const linkedMembershipRef = doc(firestore, 'memberships', (userProfile as any).membershipId);
        const linkedMembershipSnap = await getDoc(linkedMembershipRef);
        
        if (linkedMembershipSnap.exists()) {
          const linkedMembershipData = linkedMembershipSnap.data() as Membership;
          
          // Check if this is a secondary membership (has primaryMembershipId)
          // If so, load the primary membership instead
          let finalMembershipData: Membership;
          if (linkedMembershipData.primaryMembershipId) {
            const primaryMembershipRef = doc(firestore, 'memberships', linkedMembershipData.primaryMembershipId);
            const primaryMembershipSnap = await getDoc(primaryMembershipRef);
            if (primaryMembershipSnap.exists()) {
              finalMembershipData = primaryMembershipSnap.data() as Membership;
            } else {
              finalMembershipData = linkedMembershipData;
            }
          } else {
            finalMembershipData = linkedMembershipData;
          }
          
          setMembership(finalMembershipData);

          // Primary first, then any dependent — the modal swipes between them.
          // The relationship is exactly one deep, so this is a finite pair
          // rather than a list needing pagination.
          const holders: CardHolder[] = [];

          /** Load a membership's profile and add it as a card, when it has one. */
          const addHolder = async (member: Membership, isPrimary: boolean) => {
            if (!member.profileId) return;
            const profileSnap = await getDoc(
              doc(firestore, 'profiles', member.profileId),
            );
            if (!profileSnap.exists()) return;
            const memberProfile = profileSnap.data() as Profile;
            if (isPrimary) setProfile(memberProfile);
            holders.push({ membership: member, profile: memberProfile, isPrimary });
          };

          await addHolder(finalMembershipData, true);

          if (finalMembershipData.secondaryMembershipId) {
            const secondarySnap = await getDoc(
              doc(firestore, 'memberships', finalMembershipData.secondaryMembershipId),
            );
            if (secondarySnap.exists()) {
              await addHolder(
                { id: secondarySnap.id, ...secondarySnap.data() } as Membership,
                false,
              );
            }
          }

          setCardHolders(holders);
        }
      }

      setShowCardDialog(true);
    } catch (error) {
      console.error('Error loading card:', error);
    }
  };

  if (loading) {
    return <Skeleton className="w-24 h-10 rounded-md" />;
  }

  if (!user) {
    return (
      <Button variant="outline" asChild>
        <Link href="/login">
          <LogIn className="mr-2 h-4 w-4" />
          Login
        </Link>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-10 w-auto pl-2 pr-2">
          <Avatar className="h-8 w-8 mr-2">
            <AvatarImage
              src={user.photoURL || undefined}
              alt={user.displayName || 'User'}
            />
            <AvatarFallback>
              {user.displayName
                ? user.displayName.charAt(0)
                : user.email?.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="hidden sm:inline-block">
            {user.displayName || user.email}
          </span>
          <ChevronDown className="h-4 w-4 ml-1 hidden sm:inline-block" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">
              {user.displayName}
            </p>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/profile">
            <UserCircle className="mr-2 h-4 w-4" />
            <span>My Profile</span>
          </Link>
        </DropdownMenuItem>
        {user && !isEmailVerified && (
          <DropdownMenuItem asChild>
            <Link href="/verify-email" className="text-yellow-600">
              <MailCheck className="mr-2 h-4 w-4" />
              <span>Verify Email</span>
            </Link>
          </DropdownMenuItem>
        )}
        {isMember && (
          <DropdownMenuItem onClick={handleViewCard}>
            <CreditCard className="mr-2 h-4 w-4" />
            <span>View Card</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>

      {/* Full-screen membership card, covering the member and any dependent. */}
      <MemberCardModal
        open={showCardDialog}
        onOpenChange={setShowCardDialog}
        holders={cardHolders}
        user={user}
        hasMembership={isMember}
      />

    </DropdownMenu>
  );
};

const Header = () => {
  const { user, loading } = useUser();
  const firestore = useFirestore();

  const userProfileRef = useMemo(() => {
    return user ? doc(firestore, 'users', user.uid) : null;
  }, [user, firestore]);
  
  const { data: userProfile, loading: profileLoading } = useDoc(userProfileRef);

  const isAdmin = (userProfile as any)?.isAdmin;
  const isOfficer = (userProfile as any)?.isOfficer;
  const isMember = (userProfile as any)?.isMember;
  const isCD = ((userProfile as any)?.cdTypes ?? []).length > 0;
  const hasAdminAccess = isAdmin || isOfficer; // Officers also get admin access

  // Query for unread messages (members only)
  const messagesQuery = useMemo(() => {
    if (!user || !isMember) return null;
    return query(
      collection(firestore, 'messages'),
      where('receiverId', '==', user.uid),
      where('status', '==', 'new')
    );
  }, [firestore, user, isMember]);

  const { data: unreadMessages } = useCollection(messagesQuery);
  const unreadCount = (unreadMessages as Message[] | null)?.length || 0;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="w-full flex h-16 items-center justify-between px-4 md:px-6 lg:px-8">
        {/* Left: Sidebar toggle + Logo + Weather + Navigation */}
        <div className="flex items-center gap-3">
          <Sidebar
            isAuthenticated={!!user}
            isAdmin={hasAdminAccess}
            isMember={isMember}
            isOfficer={isOfficer}
            isCD={isCD}
            userId={user?.uid}
          />
          <Link
            href="/"
            className="flex items-center gap-2"
            aria-label="SRAC Home"
          >
            <Logo className="h-10 w-10" />
            <span className="hidden font-bold sm:inline-block font-headline text-primary">
              SRAC
            </span>
          </Link>

          {/* Weather indicator */}
          <div className="hidden sm:block border-l pl-3 ml-1">
            <WeatherIndicator />
          </div>

          {/* Core navigation - left aligned */}
          <nav className="hidden md:flex gap-1 ml-4">
            {coreNavLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Button key={link.href} variant="link" asChild>
                  <Link
                    href={link.href}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Icon className="h-4 w-4 mr-1.5" />
                    {link.label}
                  </Link>
                </Button>
              );
            })}
          </nav>

          {/* Member-only navigation - left aligned */}
          {isMember && (
            <nav className="hidden lg:flex gap-2 ml-2">
              <Button variant="ghost" size="sm" asChild>
                <Link href="/marketplace" className="flex items-center gap-2 text-primary hover:text-primary/80">
                  <ShoppingCart className="h-4 w-4" />
                  <span className="hidden xl:inline">Marketplace</span>
                </Link>
              </Button>
              <Button variant="ghost" size="sm" asChild className="relative">
                <Link href="/messages" className="flex items-center gap-2 text-primary hover:text-primary/80">
                  <Inbox className="h-4 w-4" />
                  <span className="hidden xl:inline">Messages</span>
                  {unreadCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                    >
                      {unreadCount}
                    </Badge>
                  )}
                </Link>
              </Button>
            </nav>
          )}
        </div>

        {/* Right: Social + User menu */}
        <div className="flex items-center gap-2">
          <SocialMediaLinks className="flex items-center gap-2" />
          <UserNav />
        </div>
      </div>
    </header>
  );
};

export default Header;
